import * as React from 'react';
import classnames from 'classnames';

import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ViewportTracker from 'components/viewport-tracker/ViewportTracker';
import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';

import ClientApprovalEntrySubtitle from '../client-approval-entry-subtitle/ClientApprovalEntrySubtitle';
import ClientApprovalEntryNote from '../client-approval-entry-note/ClientApprovalEntryNote';

import { __t, applyTranslations } from 'utils/language';

import type { ClientApprovalBucketV2 } from 'types';

import './client-approval-entry-notes.scss';

applyTranslations(require('./client-approval-entry-notes.i18n.json'));

type Props = {
  isExpandable: boolean,
  item: ClientApprovalBucketV2,
  onMessagesChecked: (ids: Array<number>) => void,
  onSendMessage: (message: string) => void
};

const ClientApprovalEntryNotes: React.FC<Props> = (
  {
    isExpandable,
    item,
    onMessagesChecked,
    onSendMessage
  }: Props
) => {
  const [isCollapsed, setIsCollapsed] = React.useState(isExpandable);
  const [message, setMessage] = React.useState('');
  const [wasObserverTriggered, setObserverTriggered] = React.useState(false);

  const newNotesFromBrand = item.approvalNotes
    .filter((note) => note.author.type === 'brand' && note.isNew);

  const headerTitleClasses = classnames('client-approval-entry-notes__header-title', {
    'client-approval-entry-notes__header-title--with-notification': newNotesFromBrand.length > 0
  });

  const iconClasses = classnames('client-approval-entry-notes__collapsible-icon', {
    'client-approval-entry-notes__collapsible-icon--open': !isCollapsed
  });

  return (
    <div className="client-approval-entry-notes">
      <div
        className="client-approval-entry-notes__header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className={headerTitleClasses}>
          <ClientApprovalEntrySubtitle>
            {__t('client_approval_entry_notes.title')}
          </ClientApprovalEntrySubtitle>
        </div>
        {isExpandable && (
          <div
            className={iconClasses}
          >
            <SVGDynamicIcon
              name="chevron-down-custom-fg"
              height={8}
              width={15}
              customColor="black"
              clickable
            />
          </div>
        )}
      </div>
      <CollapsibleContainer
        key={`${item.id}-section-for-${item.approvalNotes.length}-items`}
        isExpanded={!isCollapsed || !isExpandable}
        contentClassName="client-approval-entry-notes__collapsible-content"
      >
        <ViewportTracker
          onTrigger={() => {
            if (newNotesFromBrand.length && !wasObserverTriggered) {
              onMessagesChecked(newNotesFromBrand.map(note => note.id));
              setObserverTriggered(true);
            }
          }}
        >
          <div className="client-approval-entry-notes__messages-container">
            {
              item.approvalNotes.map((note) => (
                <ClientApprovalEntryNote
                  key={`note-${note.id}`}
                  note={note}
                />
              ))
            }
          </div>
        </ViewportTracker>
        <div className="client-approval-entry-notes__input-container">
          <Input
            value={message}
            width="responsive"
            placeholder={__t('client_approval_entry_notes.input_placeholder')}
            onChange={(event) => setMessage(event.currentTarget.value)}
          />
          <ActionButton
            color="secondary"
            onClick={() => {
              if (message !== '') {
                onSendMessage(message);
                setMessage('');
              }
            }}
          >
            {__t('client_approval_entry_notes.send_message_label')}
          </ActionButton>
        </div>
      </CollapsibleContainer>
    </div>
  );
};

export default ClientApprovalEntryNotes;
